/**
 * DOM walker that produces the Playwright-style aria outline consumed by the
 * outfitter assistant. Kept in a dedicated module so
 * `assistant-page-snapshot.ts` can expose only the public entry point
 * (`captureAssistantPageSnapshot`) and stay under the file-size cap.
 */
import {
    OUTFITTER_ASSISTANT_EXCLUDE_ATTR,
    OUTFITTER_ASSISTANT_SNAPSHOT_CHAR_CAP,
} from "~components/outfitter-assistant/outfitter-assistant-constants"
import {
    describeNode,
    resolveRole,
    shortText,
} from "~utils/helpers/assistant-page-snapshot-describe"

const TRUNCATION_MARKER = "...[truncated]"
const MAX_TABLE_ROWS = 20
const MAX_LIST_ITEMS = 50
const MAX_DEPTH = 24
const INDENT_UNIT = "  "
const LINE_PREFIX = "- "
const TABLE_ROW_CELL_SELECTOR =
    'th[scope="row"], [role=rowheader], td, [role=cell]'
const TABLE_COLUMN_HEADER_SELECTOR = "th, [role=columnheader]"
const TABLE_ROW_SELECTOR = "tbody tr, tr, [role=row]"

export class OutlineWalker {
    public truncated = false

    private readonly lines: string[] = []

    private charCount = 0

    public walk(node: Element, depth: number): void {
        if (this.truncated || depth > MAX_DEPTH || !shouldVisit(node)) return

        const emitted = this.emitLine(node, depth)
        const childDepth = emitted ? depth + 1 : depth

        if (isTable(node)) {
            this.walkTable(node, childDepth)
            return
        }

        if (isList(node)) {
            this.walkList(node, childDepth)
            return
        }

        this.walkChildren(node, childDepth)
    }

    public toString(): string {
        const joined = this.lines.join("\n")
        return this.truncated ? `${joined}\n${TRUNCATION_MARKER}` : joined
    }

    private walkChildren(node: Element, depth: number): void {
        Array.from(node.children).some((child) => {
            this.walk(child, depth)
            return this.truncated
        })
    }

    private walkTable(table: Element, depth: number): void {
        const headers = extractTableHeaders(table)
        const rows = extractTableRows(table)
        const indent = INDENT_UNIT.repeat(depth)

        if (headers.length > 0) {
            this.push(`${indent}${LINE_PREFIX}columns: ${headers.join(" | ")}`)
        }
        this.pushTableRows(rows, indent)
        if (rows.length > MAX_TABLE_ROWS) {
            this.push(
                `${indent}${LINE_PREFIX}(+${
                    rows.length - MAX_TABLE_ROWS
                } more rows)`
            )
        }
    }

    private pushTableRows(rows: string[][], indent: string): void {
        rows.slice(0, MAX_TABLE_ROWS).some((cells) => {
            if (cells.length === 0) return false
            this.push(`${indent}${LINE_PREFIX}row: ${cells.join(" | ")}`)
            return this.truncated
        })
    }

    private walkList(list: Element, depth: number): void {
        const items = Array.from(list.children).filter(isListItem)
        items.slice(0, MAX_LIST_ITEMS).some((item) => {
            this.walk(item, depth)
            return this.truncated
        })
        if (items.length > MAX_LIST_ITEMS) {
            this.push(
                `${INDENT_UNIT.repeat(depth)}${LINE_PREFIX}(+${
                    items.length - MAX_LIST_ITEMS
                } more items)`
            )
        }
    }

    private emitLine(node: Element, depth: number): boolean {
        const line = describeNode(node)
        if (!line) return false
        return this.push(`${INDENT_UNIT.repeat(depth)}${LINE_PREFIX}${line}`)
    }

    private push(line: string): boolean {
        const nextCount = this.charCount + line.length + 1
        if (nextCount > OUTFITTER_ASSISTANT_SNAPSHOT_CHAR_CAP) {
            this.truncated = true
            return false
        }
        this.lines.push(line)
        this.charCount = nextCount
        return true
    }
}

function shouldVisit(node: Element): boolean {
    if (node.hasAttribute(OUTFITTER_ASSISTANT_EXCLUDE_ATTR)) return false
    if (node.getAttribute("aria-hidden") === "true") return false
    if (node.hasAttribute("hidden")) return false
    return isDisplayed(node)
}

function isDisplayed(node: Element): boolean {
    const view = node.ownerDocument.defaultView
    if (!view) return true
    const style = view.getComputedStyle(node)
    if (style.display === "none") return false
    const { visibility } = style
    return visibility !== "hidden" && visibility !== "collapse"
}

function isTable(node: Element): boolean {
    return node.tagName === "TABLE" || resolveRole(node) === "table"
}

function isList(node: Element): boolean {
    const tag = node.tagName
    return tag === "UL" || tag === "OL" || resolveRole(node) === "list"
}

function isListItem(node: Element): boolean {
    return node.tagName === "LI" || resolveRole(node) === "listitem"
}

function extractTableHeaders(table: Element): string[] {
    const headers = Array.from(
        table.querySelectorAll(
            `thead ${TABLE_COLUMN_HEADER_SELECTOR}, [role=rowgroup] ${TABLE_COLUMN_HEADER_SELECTOR}`
        )
    )
    if (headers.length > 0) {
        return headers.map(shortText).filter(Boolean)
    }
    const firstRow = table.querySelector(TABLE_ROW_SELECTOR)
    if (!firstRow) return []
    return Array.from(firstRow.querySelectorAll(TABLE_COLUMN_HEADER_SELECTOR))
        .map(shortText)
        .filter(Boolean)
}

function extractTableRows(table: Element): string[][] {
    const rowNodes = Array.from(
        table.querySelectorAll(TABLE_ROW_SELECTOR)
    ).filter((row) => row.querySelector(TABLE_ROW_CELL_SELECTOR))
    return rowNodes.map((row) =>
        Array.from(row.querySelectorAll(TABLE_ROW_CELL_SELECTOR))
            .map(shortText)
            .filter(Boolean)
    )
}
